import React, { useEffect, useState } from "react";
import { Button, Title } from "rbx";
import PropTypes from "prop-types";
import { useQuery } from "@apollo/client";
import Select from 'react-select';
import { GET_REVIEWS, GET_EVALUATION_OWNERS, GET_REVIEWS_WITH_ONLY_EVALUATIONS } from "./graphql";

import { Loader } from "../../components";
import IncompleteReviews from "./IncompleteReviews";
import CompletedReviews from "./CompletedReviews";
import "./Reviews.scss";

const Reviews = ({ employeeNumber }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedOwner, setSelectedOwner] = useState("");
  const [isCompletedReviews, setIsCompletedReviews] = useState(false);

  // React-select states
  const [isClearable, setIsClearable] = useState(false);
  const [isSearchable, setIsSearchable] = useState(true);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRtl, setIsRtl] = useState(false);
  const lazyLoad = true;
  // Fetch all unique owners for the dropdown filter
  const {
    data: ownersData,
    loading: ownersLoading
  } = useQuery(GET_EVALUATION_OWNERS, {
    variables: {
      authorEmployeeNumber: employeeNumber
    },
    fetchPolicy: "cache-and-network",
  });

  // Prepare query variables: include reviews where user is author (has action), owner (subject of review),
  // or formAuthor (e.g. completed peer eval) so completed reviews still show after author is reassigned for sign-off
  const queryVariables = lazyLoad ?
    {
      where: {
        AND: [
          {
            dateToCompleteBy: {
              startsWith: selectedYear
            }
          },
          {
            OR: [
              {
                evaluations: {
                  some: {
                    authorEmployeeNumber: {
                      contains: employeeNumber
                    }
                  }
                }
              },
              {
                evaluations: {
                  some: {
                    ownerEmployeeNumber: {
                      contains: employeeNumber
                    }
                  }
                }
              },
              {
                evaluations: {
                  some: {
                    formAuthorEmployeeNumber: {
                      contains: employeeNumber
                    }
                  }
                }
              }
            ]
          }
        ]
      },
    } :
    {
      where: {
        AND: [
          {
            dateToCompleteBy: {
              startsWith: selectedYear
            }
          },
          {
            OR: [
              {
                evaluations: {
                  some: {
                    authorEmployeeNumber: {
                      contains: employeeNumber
                    }
                  }
                }
              },
              {
                evaluations: {
                  some: {
                    ownerEmployeeNumber: {
                      contains: employeeNumber
                    }
                  }
                }
              },
              {
                evaluations: {
                  some: {
                    formAuthorEmployeeNumber: {
                      contains: employeeNumber
                    }
                  }
                }
              }
            ]
          }
        ]
      },
      orderBy: [
        {
          name: "asc",
        },
      ],
      sectionsOrderBy: [{ order: "asc" }],
      questionsOrderBy: [{ order: "asc" }],
    };



  const {
    data: ReviewData,
    loading,
    refetch,
  } = useQuery(lazyLoad ? GET_REVIEWS_WITH_ONLY_EVALUATIONS : GET_REVIEWS, {
    variables: queryVariables,
    fetchPolicy: "cache-and-network",
  });

  const [reviews, setReviews] = useState([]);
  const [owners, setOwners] = useState([]);
  const [ownerOptions, setOwnerOptions] = useState([]);

  // Year options for the dropdown - dynamically generate based on current year
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((year) => ({
    value: year.toString(),
    label: year.toString()
  }));

  useEffect(() => {
    if (ReviewData?.reviews?.length) {
      setReviews(ReviewData.reviews);
    }
  }, [ReviewData, refetch]);

  useEffect(() => {
    if (ownersData?.evaluations?.length) {
      // Filter unique owners by ownerEmployeeNumber
      const uniqueOwners = [];
      const ownerEmployeeNumbers = new Set();

      ownersData.evaluations.forEach(evaluation => {
        if (evaluation.owner && evaluation.ownerEmployeeNumber && !ownerEmployeeNumbers.has(evaluation.ownerEmployeeNumber)) {
          ownerEmployeeNumbers.add(evaluation.ownerEmployeeNumber);
          uniqueOwners.push(evaluation);
        }
      });

      setOwners(uniqueOwners);

      // Create options for react-select
      const options = [];
      uniqueOwners.forEach(ownerItem => {
        options.push({
          value: ownerItem.owner,
          label: ownerItem.owner
        });
      });
      setOwnerOptions(options);

      // Set the first owner as default if no owner is selected
      if (!selectedOwner && uniqueOwners.length > 0) {
        setSelectedOwner(uniqueOwners[0].owner);
      }
    }
  }, [ownersData, selectedOwner]);

  if (loading || ownersLoading) {
    return (
      <div className="inner-container">
        <Loader />
      </div>
    );
  }

  // if (!reviews.length) {
  //   return (
  //     <div className="inner-container">
  //       <div className="reviews">
  //         <Title>No Reviews Found</Title>
  //       </div>
  //     </div>
  //   );
  // }

  // Handle year select change
  const handleYearChange = (selectedOption) => {
    setSelectedYear(selectedOption.value);
  };

  // Handle owner select change
  const handleOwnerChange = (selectedOption) => {
    setSelectedOwner(selectedOption ? selectedOption.value : "");
  };

  return (
    <div className="container">
      <div className="inner-container">
        <div className="button-group">
          <Button
            className={isCompletedReviews ? "unselectedBtn" : "selectedBtn"}
            type="button"
            onClick={() => setIsCompletedReviews(!isCompletedReviews)}
          >
            <p>Incomplete</p>
          </Button>
          <Button
            className={isCompletedReviews ? "selectedBtn" : "unselectedBtn"}
            type="button"
            onClick={() => setIsCompletedReviews(!isCompletedReviews)}
          >
            <p>Completed</p>
          </Button>
        </div>
        <div className="filters-container">
          <Select
            className="basic-single year-dropdown"
            classNamePrefix="select"
            value={yearOptions.find(option => option.value === selectedYear)}
            isDisabled={isDisabled}
            isLoading={isLoading}
            isClearable={false}
            isRtl={isRtl}
            isSearchable={isSearchable}
            name="year"
            options={yearOptions}
            onChange={handleYearChange}
          />

        </div>
      </div>

      {isCompletedReviews ? (
        <CompletedReviews
          employeeNumber={employeeNumber}
          loading={loading}
          reviews={reviews}
          setReviews={setReviews}
        />
      ) : (
        <IncompleteReviews
          employeeNumber={employeeNumber}
          loading={loading}
          refetchMyReviews={refetch}
          reviews={reviews}
          setReviews={setReviews}
        />
      )}
    </div>
  );
};

Reviews.propTypes = {
  employeeNumber: PropTypes.string.isRequired,
};

export default Reviews;
